/**
 * InvoiceView (E4b Item 4) — the client-facing invoice portal.
 *
 * The balance due sits in the HEADER, opposite the invoice number, so a client
 * opening this on a phone sees what they owe without scrolling. The date shown
 * is the INVOICE date, never the move date — move dates aren't fixed and putting
 * one on an invoice invites an argument about which one is binding.
 *
 * DISCOUNTS NEVER APPEAR AS LINE ITEMS. Lines print their gross amount and the
 * totals block carries every discount — per-line and order-level combined — so
 * the arithmetic reconciles exactly: gross subtotal − discounts = discounted
 * subtotal, + tax = invoice total.
 *
 * THE CARD FEE IS NEVER A LITERAL. cardFee and cardTotal are computed server-side
 * by ccFeeOn(balance, company.cc_fee_rate); the rate is only ever printed as data
 * read back from that payload. Nothing here knows the number is 3%.
 *
 * When fully paid the balance becomes a PAID badge and "How to pay" is removed.
 */

import React from 'react';
import { formatPhone, phoneOrDash, telHref } from '../../lib/format';
import { PortalActions } from './PortalActions';
import { formatMoney, formatDate } from '../../lib/utils';
import { Icons } from '../../lib/icons';
import { downloadPortalPdf, DownloadState } from './downloadPdf';
import { formatDocNo } from '../../lib/jobNumber';
import { chargeRule } from './chargeRule';

function paymentLabel(p: any): string {
  const kind = p.payment_type === 'refund' ? 'Refund' : p.payment_type === 'deposit' ? 'Deposit' : 'Payment';
  return p.method ? `${kind} · ${p.method}` : kind;
}

/**
 * `preview`: rendered inside the CRM (Finalize → "Preview invoice") from an
 * unsaved draft. Same layout, no actions — there is no PDF or link to act on yet.
 */
export function InvoiceView({ data, pdfPath, preview = false }: { data: any; pdfPath: string; preview?: boolean }) {
  // P4 — PDF download state. Failure shows INLINE beside the button: a portal is
  // a client-facing page and an alert() is not an acceptable error surface.
  // The action row itself is hidden in the PDF by the stylesheet's @media print
  // rule, which Chromium honours via emulateMediaType('print') — one mechanism,
  // not a JSX conditional doing the same job a second way.
  const [pdf, setPdf] = React.useState<DownloadState>({ busy: false, error: null });
  const onDownload = async () => {
    setPdf({ busy: true, error: null });
    setPdf({ busy: false, error: await downloadPortalPdf(pdfPath) });
  };
  const inv = data.invoice;
  const job = data.job ?? {};
  const pm = inv.paymentMethods ?? {};
  const phone = data.company?.phone || '647 931 5244';

  return (
    <>
      {/* 1. Header — identity left, what they owe right. */}
      <div className="pp-head">
        <div>
          <div className="pp-kicker">Baltic Van Lines</div>
          <h1 className="pp-h1">Invoice{inv.issueDate ? ` · ${formatDate(String(inv.issueDate).slice(0, 10))}` : ''}</h1>
          {/* ONE identifier. This printed both the job number and the document
              number — "#1000 · #1000-6" — and a client reading two numbers does
              not know which to quote. The document number already CONTAINS the
              job number, so it is the one to show. */}
          <div className="pp-jobno">{formatDocNo(inv.number) !== '—' ? formatDocNo(inv.number) : `#${job.number}`}</div>
        </div>
        <div className="pp-balance">
          {inv.isPaid ? (
            <span className="pp-paid">PAID</span>
          ) : (
            <>
              <div className="pp-balance-l">Balance due</div>
              <div className="pp-balance-v">{formatMoney(inv.balanceDue)}</div>
            </>
          )}
        </div>
      </div>

      {/* 2. Billed to */}
      <div className="pp-card">
        <div className="pp-sec">Billed to</div>
        <div className="pp-tiles">
          <div className="pp-tile"><div className="pp-tile-l">Name</div><div className="pp-tile-v sm">{inv.billTo?.name || '—'}</div></div>
          <div className="pp-tile"><div className="pp-tile-l">Phone</div><div className="pp-tile-v sm">{phoneOrDash(inv.billTo?.phone)}</div></div>
          <div className="pp-tile"><div className="pp-tile-l">Email</div><div className="pp-tile-v sm">{inv.billTo?.email || '—'}</div></div>
        </div>
      </div>

      {/* 3. Addresses — origin stacked ABOVE destination, no service type. */}
      <div className="pp-card">
        <div className="pp-sec">Origin</div>
        <div className="pp-addr">{inv.origin?.address || '—'}</div>
        <div className="pp-sec" style={{ marginTop: 16 }}>Destination</div>
        <div className="pp-addr">{inv.destination?.address || '—'}</div>
      </div>

      {/* 4 + 5. Charges, then the totals block. */}
      <div className="pp-card">
        <div className="pp-sec">Charges</div>
        {/* Finalize: what the job turned out to be. Absent on older invoices. */}
        {(inv.finalWeightLbs != null || inv.finalHours != null) && (
          <div className="pp-sub" style={{ marginBottom: 10 }}>
            {inv.finalWeightLbs != null && <>Final weight {Number(inv.finalWeightLbs).toLocaleString()} lb</>}
            {inv.finalHours != null && <>{Number(inv.finalHours).toLocaleString()} hours{inv.finalCrewSize ? ` · crew of ${Number(inv.finalCrewSize)}` : ''}</>}
          </div>
        )}
        <div className="pp-wrap">
          <table className="pp-tbl">
            <tbody>
              {(inv.items ?? []).map((it: any, i: number) => {
                const rule = chargeRule(it);
                return (
                  <tr key={i}>
                    <td>
                      {it.desc}
                      {rule && <div className="pp-rule">{rule}</div>}
                      {it.note && <div className="pp-rule">{it.note}</div>}
                    </td>
                    <td className="pp-amt">{formatMoney(it.gross)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="pp-totals">
          <div className="pp-trow"><span>Subtotal</span><span>{formatMoney(inv.grossSubtotal)}</span></div>
          {Number(inv.allDiscountAmt) > 0 && (
            <div className="pp-trow"><span>Discounts</span><span>−{formatMoney(inv.allDiscountAmt)}</span></div>
          )}
          <div className="pp-trow">
            <span>Tax{inv.taxRate ? ` (${Number(inv.taxRate)}%)` : ''}</span>
            <span>{formatMoney(inv.tax)}</span>
          </div>
          {/* After tax, before the card fee: non-taxable adjustments and the tip.
              Both are in the invoice total, so the block still adds up. */}
          {(inv.afterTax ?? []).map((r: any, i: number) => (
            <div className="pp-trow" key={`at${i}`}>
              <span>{r.label}{r.kind === 'tip' && <span style={{ color: '#8b93a1' }}> · not taxed</span>}</span>
              <span>{Number(r.amount) < 0 ? `−${formatMoney(Math.abs(Number(r.amount)))}` : formatMoney(r.amount)}</span>
            </div>
          ))}
          {/* Placement is correct as-is: AFTER tax, because the fee is not
              taxable. What was missing is that it reads like a charge everyone
              pays. The row only exists when a card payment was actually taken,
              so it is card-only by construction — it now says so. */}
          {Number(inv.ccFeeTotal) > 0 && (
            <div className="pp-trow">
              <span>Card fee <span style={{ color: '#8b93a1' }}>· on card payments only, not taxed</span></span>
              <span>{formatMoney(inv.ccFeeTotal)}</span>
            </div>
          )}
          <div className="pp-trow grand"><span>Invoice total</span><span>{formatMoney(inv.total)}</span></div>
        </div>
      </div>

      {/* 6. Payments applied */}
      {(inv.payments ?? []).length > 0 && (
        <div className="pp-card">
          <div className="pp-sec">Payments applied</div>
          <div className="pp-wrap">
            <table className="pp-tbl">
              <tbody>
                {inv.payments.map((p: any, i: number) => (
                  <tr key={i}>
                    <td>
                      {p.paid_on ? formatDate(p.paid_on) : '—'}
                      <div className="pp-rule">
                        {paymentLabel(p)}
                        {Number(p.cc_fee_amount) > 0 ? ` · incl. ${formatMoney(p.cc_fee_amount)} card fee` : ''}
                      </div>
                    </td>
                    <td className="pp-amt">
                      {p.payment_type === 'refund' ? '−' : ''}
                      {formatMoney(Number(p.amount) + Number(p.cc_fee_amount || 0))}
                    </td>
                  </tr>
                ))}
                <tr className="pp-tot">
                  <td>{inv.isPaid ? 'Balance' : 'Balance due'}</td>
                  <td className="pp-amt" style={inv.isPaid ? { color: '#0f8a4c' } : undefined}>
                    {inv.isPaid ? 'Paid in full' : formatMoney(inv.balanceDue)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 7. Payment terms — a callout, not a footnote. Stays even when paid. */}
      <div className="pp-card">
        <div className="pp-callout">
          <span style={{ color: '#b26f00', flexShrink: 0, marginTop: 1 }}><Icons.warning size={18} aria-hidden="true" /></span>
          <div>
            <div className="pp-callout-t">Payment terms</div>
            {/* THE TERM, WORD FOR WORD. The previous wording said payment was due
                before a delivery date could be SCHEDULED, which is a different
                and stricter obligation than the one the contract and the
                confirmation form state — the client's own signed documents said
                48 hours before delivery. The invoice is the document they pay
                from, so it was the one place the term was wrong. */}
            {/* Local moves are paid with the crew on site; the 48-hour term is
                long distance only. `local` is absent on a payload from before
                it existed, which reads as long distance — today's text. */}
            <div className="pp-callout-b">
              {inv.local
                ? 'Payment will be processed with the crew on site, before move completion.'
                : 'Payment is due in full at minimum 48 hours before scheduled delivery.'}
            </div>
          </div>
        </div>
      </div>

      {/* 8. How to pay — removed entirely once the invoice is settled. */}
      {!inv.isPaid && (
        <div className="pp-card">
          <div className="pp-sec">How to pay</div>
          <div className="pp-legs">
            <div className="pp-card accent" style={{ margin: 0 }}>
              <div className="pp-tile-l" style={{ color: '#b26f00' }}>Preferred · no fee</div>
              <div className="pp-addr" style={{ fontSize: 17, marginTop: 3 }}>E-transfer</div>
              <div className="pp-sub" style={{ marginTop: 6, overflowWrap: 'anywhere' }}>
                Send to <b>{pm.eTransferEmail}</b>
              </div>
              <div className="pp-trow" style={{ marginTop: 8, fontWeight: 700 }}>
                <span>Amount</span><span>{formatMoney(inv.balanceDue)}</span>
              </div>
            </div>

            <div className="pp-card" style={{ margin: 0 }}>
              <div className="pp-tile-l">Card</div>
              <div className="pp-addr" style={{ fontSize: 17, marginTop: 3 }}>Credit card</div>
              <div className="pp-trow" style={{ marginTop: 8 }}><span>Balance</span><span>{formatMoney(inv.balanceDue)}</span></div>
              <div className="pp-trow"><span>{Number(pm.ccRate)}% processing fee</span><span>{formatMoney(pm.cardFee)}</span></div>
              <div className="pp-trow" style={{ fontWeight: 800, borderTop: '1px solid #e3e6ec', marginTop: 4, paddingTop: 8 }}>
                <span>Total by card</span><span>{formatMoney(pm.cardTotal)}</span>
              </div>
            </div>
          </div>
          <p className="pp-sub" style={{ marginTop: 12 }}>
            The processing fee is not subject to tax and applies only to card payments.
          </p>
        </div>
      )}

      {/* 9. Footer */}
      {!preview && <PortalActions>
        <a className="pp-btn" href={telHref(phone)}>
          <Icons.phone size={16} aria-hidden="true" /> {formatPhone(phone)}
        </a>
        {/* P4: the server prints THIS page, so the file matches what you see. */}
        <button className="pp-btn" onClick={onDownload} disabled={pdf.busy}>
          <Icons.document size={16} aria-hidden="true" /> {pdf.busy ? 'Preparing…' : 'Download PDF'}
        </button>
      </PortalActions>}
      {pdf.error && <div className="pp-pdf-error pp-noprint">{pdf.error}</div>}
    </>
  );
}
